interface Params {
    rows: number;
    columns: number;
    width: number;
    height: number;
    font: {
        size: number;
        family: string;
    };
    lineWidth: number;
    distToCenterNumber: number;
    scale: number;
    elementsOnRow: number;
    xSpaceBetweenElements: number;
    ySpaceBetweenElements: number;
    lines: number[][];
    colors: {
        symbolCell: string;
        emptyCell: string;
    };
}

interface Point {
    x: number;
    y: number;
}

interface Size {
    width: number;
    height: number;
}

export class PayLinesGenerator {
    private lines: number[][];
    private colors: {
        symbolCell: string;
        emptyCell: string;
    };
    private rows: number;
    private columns: number;
    private canvas!: HTMLCanvasElement;
    private ctx!: CanvasRenderingContext2D;
    private currPos!: Point;
    private lineWidth: number;
    private size: Size;
    private font: {
        size: number;
        family: string;
    };
    private scale: number;
    private distToCenterNumber: number;
    private elementsOnRow: number;
    private xSpaceBetweenElements = 100;
    private ySpaceBetweenElements = 100;

    constructor(params: Params) {
        this.rows = params.rows;
        this.columns = params.columns;
        this.scale = params.scale;
        this.font = params.font;
        this.lineWidth = params.lineWidth;
        this.distToCenterNumber = params.distToCenterNumber;
        this.xSpaceBetweenElements = params.xSpaceBetweenElements;
        this.ySpaceBetweenElements = params.ySpaceBetweenElements;
        this.elementsOnRow = params.elementsOnRow;
        this.lines = params.lines;
        this.colors = params.colors;
        this.size = {
            width: params.width + this.lineWidth,
            height: params.height + this.lineWidth
        };
    }

    public generatePayLines(): HTMLCanvasElement {
        this.createCanvas();

        let x = this.currPos.x;
        let y = this.currPos.y;
        const startX = x;
        let prevX = x;
        let prevY = y;

        for (let line = 0; line < this.lines.length; line++) {
            const textWidth: number = this.getTextWidth((line + 1).toString());

            if (this.isNewLine(line)) {
                y += this.size.height * this.rows + this.ySpaceBetweenElements + this.lineWidth;
                prevY = y;
                x = startX;
                prevX = x;
            }

            // TODO: write method that will calculate the text height dynamically
            this.drawNumber(line + 1, {
                x: x - this.lineWidth / 2 - textWidth / 2 - this.distToCenterNumber,
                y: y - this.lineWidth / 2 + (this.size.height * this.rows + this.lineWidth * 2) / 2 + this.font.size / this.rows
            });

            for (let i = 0; i < this.rows; i++) {
                x = prevX;
                for (let j = 0; j < this.columns; j++) {
                    const color = this.getColor(line, i, j);

                    this.drawCube({x, y}, color);

                    x += this.size.width;
                }
                y += this.size.height;
            }

            y = prevY;
            x += this.xSpaceBetweenElements + this.lineWidth;
            prevX = x;
        }

        return this.canvas;
    }

    private createCanvas(): void {
        this.canvas = document.createElement('canvas');
        this.ctx = this.canvas.getContext('2d')!;

        const maxTextWidth = this.getTextWidth(this.lines.length.toString());
        const countRows = Math.round(this.lines.length / this.elementsOnRow);
        const width = this.elementsOnRow * ((this.size.width - this.lineWidth) * this.columns +
            ((this.columns + 1) * this.lineWidth)) + (this.elementsOnRow - 1) * this.xSpaceBetweenElements;
        const height = countRows * ((this.size.height - this.lineWidth) * this.rows +
            ((this.rows + 1) * this.lineWidth)) + (countRows - 1) * this.ySpaceBetweenElements;
        const textOffset = maxTextWidth + (this.distToCenterNumber - maxTextWidth / 2);
        const padding = 20;

        this.currPos = {x: textOffset + padding / 2, y: padding / 2};
        this.canvas.width = (width + textOffset + padding) * this.scale;
        this.canvas.height = (height + padding) * this.scale;
    }

    private drawNumber(num: number, pos: Point): void {
        this.ctx.textBaseline = 'alphabetic';
        this.ctx.font = `${this.font.size * this.scale}px ${this.font.family}`;
        this.ctx.fillStyle = '#ffffff';
        this.ctx.fillText(num.toString(), pos.x * this.scale, pos.y * this.scale);
    }

    private getTextWidth(text: string): number {
        this.ctx.font = `${this.font.size}px ${this.font.family}`;

        return this.ctx.measureText(text).width;
    }

    private getColor(payLineNum: number, row: number, col: number): string {
        return this.lines[payLineNum][col] === row ? this.colors.symbolCell : this.colors.emptyCell;
    }

    private drawCube(pos: Point, color: string): void {
        this.ctx.beginPath();
        this.ctx.fillStyle = color;
        this.ctx.rect(pos.x * this.scale, pos.y * this.scale, this.size.width * this.scale, this.size.height * this.scale);
        this.ctx.fill();
        this.ctx.lineWidth = this.lineWidth * this.scale;
        this.ctx.strokeStyle = '#000000';
        this.ctx.stroke();
        this.ctx.closePath();
    }

    private isNewLine(currentPayLine: number): boolean {
        while (currentPayLine >= 0) {
            currentPayLine -= this.elementsOnRow;

            if (currentPayLine === 0) {
                return true;
            }
        }

        return false;
    }
}